import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useSSEConnection } from 'context/SSE/sse-connection-context';
import { SelfStudyAssignmentResponse } from 'model/offline-class';
import {
  CreateSelfStudyAssignmentArgs,
  CreateSelfStudyAssignmentInput,
  CreateSelfStudyAssignmentResponse,
  SelfStudyAssignment,
  SelfStudyAssignmentContentPreview,
  SelfStudyAssignmentReport,
  SelfStudyAssignmentStatus,
} from 'model/self-study-assignment';
import { ddlog } from 'utils/miscellaneous';

const POLL_INTERVAL = 10000;
// With the event stream open the poll is only a backstop: an event can still miss this
// viewer, e.g. one who does not own the assignment, or a publish dropped upstream.
const STREAM_BACKSTOP_POLL_INTERVAL = 60000;
const MAX_POLL_DURATION = 15 * 60 * 1000;

export const SELF_STUDY_ASSIGNMENT = gql`
  query SelfStudyAssignment($id: ID!) {
    selfStudyAssignment(id: $id) {
      id
      teacherId
      isAllStudents
      metadata
      title
      description
      acciLessonId
      status
      error
      createdAt
      publishedAt
      class {
        id
        name
        subject
        grade
        studentCount
      }
      students {
        studentId
        profile {
          firstName
          lastName
        }
      }
      progress {
        completedCount
        totalCount
      }
    }
  }
`;

// Slim query for polling — selects only fields that change during generation.
// Apollo normalizes by `id` so writes to these fields update the same cache
// entity that the full query subscribes to.
export const SELF_STUDY_ASSIGNMENT_POLL = gql`
  query SelfStudyAssignmentPoll($id: ID!) {
    selfStudyAssignment(id: $id) {
      id
      status
      error
      publishedAt
      title
      description
    }
  }
`;

export const SELF_STUDY_ASSIGNMENTS = gql`
  query SelfStudyAssignments($filter: SelfStudyAssignmentsFilter, $pagination: Pagination) {
    selfStudyAssignments(filter: $filter, pagination: $pagination) {
      edges {
        id
        status
        isAllStudents
        metadata
        title
        description
        publishedAt
        createdAt
        class {
          id
          name
          studentCount
        }
        students {
          studentId
          profile {
            firstName
            lastName
          }
        }
        progress {
          completedCount
          totalCount
          students {
            studentId
            status
            percentComplete
            timeSpentSec
            completedAt
            profile {
              firstName
              lastName
            }
          }
        }
      }
      pageInfo {
        pages
        total
      }
    }
  }
`;

export const SELF_STUDY_ASSIGNMENT_CONTENT_PREVIEW = gql`
  query SelfStudyAssignmentContentPreview($id: ID!) {
    selfStudyAssignmentContentPreview(id: $id) {
      jsonContent
    }
  }
`;

const REPORT_AVERAGE_FIELDS = `
  average
  count
`;

const REPORT_SUMMARY_FIELDS = `
  id
  generatedAt
  aggregates {
    studentCount
    completedCount
    startedCount
    completionPct { ${REPORT_AVERAGE_FIELDS} }
    timeSpentSec { ${REPORT_AVERAGE_FIELDS} }
    quizScore { ${REPORT_AVERAGE_FIELDS} }
    quizTaskCount
    aiConversationScore { ${REPORT_AVERAGE_FIELDS} }
    aiWritingScore { ${REPORT_AVERAGE_FIELDS} }
    lessonTasks { speaking writing }
  }
`;

const REPORT_STUDENT_FIELDS = `
  students {
    studentId
    isAssigned
    status
    completionPct
    timeSpentSec
    completedAt
    quizScore
    quizTaskCount
    aiConversationScore
    aiWritingScore
    roleplaySessionId
    writingActivityId
    speakingTasks { taskId stars }
    writingTaskCount
  }
`;

/**
 * The collapsed card: aggregates only. Gipfeli fetches the student rows only when `students`
 * is selected, so this stays cheap for a list of many assignments.
 */
export const SELF_STUDY_ASSIGNMENT_REPORT_SUMMARY = gql`
  query SelfStudyAssignmentReportSummary($id: ID!) {
    selfStudyAssignmentReport(id: $id) {
      ${REPORT_SUMMARY_FIELDS}
    }
  }
`;

/**
 * The opened card. It selects the summary fields too, so the aggregates and "Last updated"
 * stay in step with the rows it brings back.
 */
export const SELF_STUDY_ASSIGNMENT_REPORT_STUDENTS = gql`
  query SelfStudyAssignmentReportStudents($id: ID!) {
    selfStudyAssignmentReport(id: $id) {
      ${REPORT_SUMMARY_FIELDS}
      ${REPORT_STUDENT_FIELDS}
    }
  }
`;

/**
 * Refresh is a mutation, not a `refresh` query variable: `refetch(variables)` would pin the
 * variable onto the query, and every later refetch (tab focus included) would bypass the
 * backend caches. The result has the report's `id`, so Apollo writes it over the cached report.
 */
export const REFRESH_SELF_STUDY_ASSIGNMENT_REPORT = gql`
  mutation RefreshSelfStudyAssignmentReport($id: ID!) {
    refreshSelfStudyAssignmentReport(id: $id) {
      ${REPORT_SUMMARY_FIELDS}
    }
  }
`;

/** Refresh for an opened card, so its rows are rebuilt along with the aggregates. */
export const REFRESH_SELF_STUDY_ASSIGNMENT_REPORT_WITH_STUDENTS = gql`
  mutation RefreshSelfStudyAssignmentReportWithStudents($id: ID!) {
    refreshSelfStudyAssignmentReport(id: $id) {
      ${REPORT_SUMMARY_FIELDS}
      ${REPORT_STUDENT_FIELDS}
    }
  }
`;

/** Query names the provider leaves out of its refetch-on-focus: the report has its own refresh. */
export const SELF_STUDY_ASSIGNMENT_REPORT_QUERY_NAMES = [
  'SelfStudyAssignmentReportSummary',
  'SelfStudyAssignmentReportStudents',
];

interface SelfStudyAssignmentReportResponse {
  selfStudyAssignmentReport: SelfStudyAssignmentReport | null;
}

interface SelfStudyAssignmentQueryResponse {
  selfStudyAssignment: SelfStudyAssignment | null;
}

interface SelfStudyAssignmentsResponse {
  selfStudyAssignments: {
    edges: SelfStudyAssignment[];
    pageInfo: { pages: number; total: number };
  } | null;
}

interface SelfStudyAssignmentContentPreviewResponse {
  selfStudyAssignmentContentPreview: SelfStudyAssignmentContentPreview | null;
}

export const CREATE_SELF_STUDY_ASSIGNMENT = gql`
  mutation CreateSelfStudyAssignment($input: CreateSelfStudyAssignmentInput!) {
    createSelfStudyAssignment(input: $input) {
      lessonId
    }
  }
`;

// The prompt fields are what the backend expects inside `metadata`; the rest map one-to-one.
export const toCreateSelfStudyAssignmentInput = ({
  acciLessonId,
  acciPrompt,
  sourceId,
  sourceType,
  isAllStudents,
  teacherId,
  studentIds,
  duration,
  subject,
  topic,
  activity,
  description,
  difficulty,
  grade,
  topicSource,
  title,
}: CreateSelfStudyAssignmentArgs): CreateSelfStudyAssignmentInput => ({
  sourceId,
  sourceType,
  acciLessonId,
  isAllStudents,
  teacherId,
  ...(acciPrompt != null && { acciPrompt }),
  ...(studentIds != null && { studentIds }),
  // Row-level, alongside the copies in `metadata`: the API renders these two,
  // and a title is what lets a pre-authored lesson skip the webhook grace period.
  ...(title != null && { title }),
  ...(description != null && { description }),
  metadata: {
    duration,
    subject,
    topic,
    description,
    ...(activity != null && { activity }),
    ...(difficulty != null && { difficulty }),
    ...(grade != null && { grade }),
    ...(topicSource != null && { topicSource }),
  },
});

/**
 * Creates a self-study assignment for an already-generated ACCI lesson, in place of the
 * Compass `createSelfStudyAssignment` (`network/Compass`) which generates one. The returned
 * function takes the same payload and resolves the same `{ resultStatus, lessonId }`,
 * throwing on failure, so what a caller sends and reads is unchanged.
 *
 * Used by the curriculum-aligned branch of the assignment modal, which attaches a
 * pre-authored lesson instead of generating content. TO-BE-removed after demo.
 */
export const useCreateSelfStudyAssignment = () => {
  const [mutate, { loading, error }] = useMutation<CreateSelfStudyAssignmentResponse>(CREATE_SELF_STUDY_ASSIGNMENT);

  const createSelfStudyAssignment = useCallback(
    async (payload: CreateSelfStudyAssignmentArgs): Promise<SelfStudyAssignmentResponse> => {
      const res = await mutate({ variables: { input: toCreateSelfStudyAssignmentInput(payload) } });

      const lessonId = res.data?.createSelfStudyAssignment?.lessonId;
      if (!lessonId) {
        // Fail rather than let a caller navigate to `/undefined`.
        throw new Error('createSelfStudyAssignment returned no lessonId');
      }

      return { resultStatus: 'ok', lessonId };
    },
    [mutate],
  );

  return { createSelfStudyAssignment, loading, error };
};

export const useSelfStudyAssignment = (assignmentId: string | undefined) => {
  const { data, loading, error, refetch } = useQuery<SelfStudyAssignmentQueryResponse>(SELF_STUDY_ASSIGNMENT, {
    variables: { id: assignmentId },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !assignmentId,
  });

  const assignment = data?.selfStudyAssignment ?? undefined;
  const isGenerating = assignment?.status === SelfStudyAssignmentStatus.GENERATING;

  // Stop polling after MAX_POLL_DURATION so a stuck generation surfaces to the
  // user instead of spinning forever.
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const pollStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isGenerating) {
      pollStartRef.current = null;
      setPollTimedOut(false);
      return;
    }
    if (pollStartRef.current === null) {
      pollStartRef.current = Date.now();
    }
    const remaining = MAX_POLL_DURATION - (Date.now() - pollStartRef.current);
    if (remaining <= 0) {
      setPollTimedOut(true);
      return;
    }
    const timer = setTimeout(() => setPollTimedOut(true), remaining);
    return () => clearTimeout(timer);
  }, [isGenerating]);

  // Slim poll keeps the cached entity fresh while generating. Its writes flow
  // back into the full query via Apollo's normalized cache.
  const { connected: streamConnected } = useSSEConnection();
  const shouldPoll = isGenerating && !pollTimedOut;
  const pollEvery = streamConnected ? STREAM_BACKSTOP_POLL_INTERVAL : POLL_INTERVAL;
  useQuery<SelfStudyAssignmentQueryResponse>(SELF_STUDY_ASSIGNMENT_POLL, {
    variables: { id: assignmentId },
    fetchPolicy: 'network-only',
    pollInterval: shouldPoll ? pollEvery : 0,
    skip: !assignmentId || !shouldPoll,
  });

  return {
    assignment,
    isLoading: loading && !assignment,
    isError: error,
    pollTimedOut,
    refetch,
  };
};

export const useSelfStudyAssignmentContentPreview = (assignmentId: string | undefined, ready: boolean) => {
  const { data, loading, error } = useQuery<SelfStudyAssignmentContentPreviewResponse>(
    SELF_STUDY_ASSIGNMENT_CONTENT_PREVIEW,
    {
      variables: { id: assignmentId },
      fetchPolicy: 'cache-first',
      skip: !assignmentId || !ready,
    },
  );

  return {
    contentPreview: data?.selfStudyAssignmentContentPreview ?? undefined,
    isLoading: ready && loading,
    isError: error,
  };
};

export const useClassSelfStudyAssignments = (classId: string | undefined) => {
  const { data, loading, error, refetch } = useQuery<SelfStudyAssignmentsResponse>(SELF_STUDY_ASSIGNMENTS, {
    variables: {
      filter: classId
        ? { sourceId: classId, sourceType: 'class', status: [SelfStudyAssignmentStatus.PUBLISHED] }
        : undefined,
      // The table has no pagination UI, so a single page has to cover the whole class.
      pagination: { size: 50 },
    },
    fetchPolicy: 'cache-and-network',
    skip: !classId,
  });

  return {
    assignments: data?.selfStudyAssignments?.edges,
    isLoading: loading && !data,
    isError: error,
    refetch,
  };
};

interface UseSelfStudyAssignmentReportOptions {
  /** Skip the request entirely - e.g. for teachers outside the pilot. */
  skip?: boolean;
}

const useSelfStudyAssignmentReportQuery = (
  query: typeof SELF_STUDY_ASSIGNMENT_REPORT_SUMMARY,
  assignmentId: string | undefined,
  skip: boolean,
) => {
  const { data, loading, error } = useQuery<SelfStudyAssignmentReportResponse>(query, {
    variables: { id: assignmentId },
    fetchPolicy: 'cache-and-network',
    skip: skip || !assignmentId,
  });
  const report = data?.selfStudyAssignmentReport ?? undefined;

  return {
    report,
    isLoading: loading && !report,
    isError: !!error && !report,
  };
};

/** The collapsed card's report: the aggregates and "Last updated". */
export const useSelfStudyAssignmentReportSummary = (
  assignmentId: string | undefined,
  { skip = false }: UseSelfStudyAssignmentReportOptions = {},
) => useSelfStudyAssignmentReportQuery(SELF_STUDY_ASSIGNMENT_REPORT_SUMMARY, assignmentId, skip);

/** The opened card's rows. Call it from the panel, so it only runs once a card is opened. */
export const useSelfStudyAssignmentReportStudents = (assignmentId: string | undefined) =>
  useSelfStudyAssignmentReportQuery(SELF_STUDY_ASSIGNMENT_REPORT_STUDENTS, assignmentId, false);

/**
 * Rebuilds the report past the backend caches. `withStudents` refreshes the rows as well, for
 * an opened card. `refreshError` clears as soon as a newer report arrives by any route, so a
 * fresh "Last updated" never sits above a stale "Couldn't refresh".
 */
export const useRefreshSelfStudyAssignmentReport = (
  assignmentId: string | undefined,
  generatedAt: string | undefined,
) => {
  const [refreshSummary] = useMutation<{ refreshSelfStudyAssignmentReport: SelfStudyAssignmentReport }>(
    REFRESH_SELF_STUDY_ASSIGNMENT_REPORT,
  );
  const [refreshWithStudents] = useMutation<{ refreshSelfStudyAssignmentReport: SelfStudyAssignmentReport }>(
    REFRESH_SELF_STUDY_ASSIGNMENT_REPORT_WITH_STUDENTS,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);

  useEffect(() => {
    setRefreshError(false);
  }, [generatedAt]);

  const refresh = useCallback(
    async ({ withStudents = false }: { withStudents?: boolean } = {}) => {
      if (!assignmentId) {
        return;
      }
      setIsRefreshing(true);
      setRefreshError(false);
      try {
        const mutate = withStudents ? refreshWithStudents : refreshSummary;
        await mutate({ variables: { id: assignmentId } });
      } catch (err) {
        // The previous report stays on screen; the card only needs to say the refresh failed.
        setRefreshError(true);
        ddlog.error('Failed to refresh self-study assignment report', { assignmentId }, err as Error);
      } finally {
        setIsRefreshing(false);
      }
    },
    [assignmentId, refreshSummary, refreshWithStudents],
  );

  return { refresh, isRefreshing, refreshError };
};

/** The opened card's AI summary, read once each time the card is opened. */
